import { useParams, Link } from 'react-router-dom';
import { animeList } from '@/mocks/anime';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AnimeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const anime = animeList.find((a) => a.id === id);

  if (!anime) {
    return (
      <div className="pt-20 pb-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center py-20">
          <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
            <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
          </div>
          <h1 className="font-heading text-2xl text-foreground-900 mb-3">404 - 找不到该动漫</h1>
          <p className="text-foreground-500 text-sm mb-6">您要查找的动漫不存在或已下架</p>
          <Link to="/" className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer inline-block">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedAnimes = animeList
    .filter((a) => a.category === anime.category && a.id !== anime.id)
    .slice(0, 6);

  const episodes = Array.from({ length: Math.min(anime.episodes, 24) }, (_, i) => ({
    num: anime.episodes - i,
    title: `第${anime.episodes - i}话`,
    date: new Date(new Date(anime.updatedAt).getTime() - i * 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  }));

  const metaTitle = `${anime.title} - 51动漫在线观看免费全集 | 51动漫网`;
  const metaDescription = `51动漫网提供${anime.title}高清在线观看服务，${anime.title}全集免费追更，下拉式阅读体验，${anime.tags.slice(0, 3).join('、')}题材动漫推荐。`;
  const metaKeywords = `51动漫网,51动漫在线观看,${anime.title},${anime.title}在线观看,${anime.title}免费全集,${anime.category},动漫追更`;

  return (
    <div className="pt-20 pb-14">
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <link rel="canonical" href={`https://www.51dongman.com/anime/${anime.id}`} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={`https://www.51dongman.com/anime/${anime.id}`} />
      <meta property="og:image" content={anime.coverImage} />
      <meta property="og:type" content="article" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={anime.coverImage} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${anime.title} - 51动漫在线观看`,
            description: anime.description,
            image: anime.coverImage,
            author: { '@type': 'Person', name: anime.author },
            datePublished: `${anime.year}-01-01`,
            dateModified: anime.updatedAt,
            publisher: { '@type': 'Organization', name: '51动漫网', url: 'https://www.51dongman.com/' },
            mainEntityOfPage: { '@type': 'WebPage', '@id': `https://www.51dongman.com/anime/${anime.id}` },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.51dongman.com/' },
              { '@type': 'ListItem', position: 2, name: anime.category, item: `https://www.51dongman.com/category/${anime.category}` },
              { '@type': 'ListItem', position: 3, name: anime.title, item: `https://www.51dongman.com/anime/${anime.id}` },
            ],
          }),
        }}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: anime.category, to: `/category/${anime.category}` },
            { label: anime.title },
          ]}
        />

        <article className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden mb-10">
          <div className="flex flex-col lg:flex-row gap-0">
            <div className="lg:w-[350px] flex-shrink-0 relative">
              <img
                src={anime.coverImage}
                alt={`${anime.title} - 51动漫网在线观看免费全集`}
                title={`${anime.title} - 51动漫在线观看`}
                className="w-full h-[400px] lg:h-full object-cover object-top"
              />
              <div className="absolute top-4 left-4 flex gap-2">
                {anime.isHot && (
                  <span className="px-3 py-1 bg-primary-500 text-white text-xs font-medium rounded-full">热门</span>
                )}
                {anime.isNew && (
                  <span className="px-3 py-1 bg-accent-500 text-white text-xs font-medium rounded-full">新番</span>
                )}
              </div>
            </div>

            <div className="flex-1 p-6 md:p-8">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">{anime.title}</h1>

              <div className="flex flex-wrap items-center gap-3 mb-5">
                <span className="flex items-center gap-1 text-accent-500 font-semibold text-lg">
                  <i className="ri-star-fill"></i> {anime.rating}
                </span>
                <span className="text-xs px-2 py-1 bg-secondary-100 text-secondary-800 rounded whitespace-nowrap">{anime.category}</span>
                <span className={`text-xs px-2 py-1 rounded whitespace-nowrap ${anime.status === '连载中' ? 'bg-accent-100 text-accent-800' : 'bg-secondary-100 text-secondary-800'}`}>
                  {anime.status}
                </span>
                <span className="text-xs text-foreground-500">{anime.episodes}话</span>
                <span className="text-xs text-foreground-500">{anime.views}次观看</span>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-6">
                {anime.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap">{tag}</span>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-500">作者</p>
                  <p className="text-sm font-medium text-foreground-800 mt-0.5">{anime.author}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-500">年份</p>
                  <p className="text-sm font-medium text-foreground-800 mt-0.5">{anime.year}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-500">最近更新</p>
                  <p className="text-sm font-medium text-foreground-800 mt-0.5">{anime.updatedAt}</p>
                </div>
              </div>

              <div className="mb-6">
                <h2 className="text-base font-semibold text-foreground-900 mb-3">剧情简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{anime.description}</p>
              </div>

              <Link
                to="#"
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-medium text-sm transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-fill text-lg"></i> 开始观看
              </Link>
            </div>
          </div>
        </article>

        <section className="mb-12">
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6">剧集列表</h2>
          <div className="bg-background-50 rounded-xl border border-background-200/70 p-4 md:p-5">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {episodes.map((ep) => (
                <a
                  key={ep.num}
                  href="#"
                  className="flex items-center justify-between px-4 py-3 bg-background-100 hover:bg-primary-50 rounded-lg transition-colors duration-200 cursor-pointer group"
                >
                  <span className="text-sm text-foreground-700 group-hover:text-primary-600 whitespace-nowrap">{ep.title}</span>
                  <span className="text-[10px] text-foreground-400">{ep.date}</span>
                </a>
              ))}
              {anime.episodes > 24 && (
                <a
                  href="#"
                  className="flex items-center justify-center px-4 py-3 bg-background-100 hover:bg-primary-50 rounded-lg transition-colors duration-200 cursor-pointer"
                >
                  <span className="text-sm text-foreground-500 whitespace-nowrap">查看更多...</span>
                </a>
              )}
            </div>
          </div>
        </section>

        {relatedAnimes.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900">相关推荐</h2>
              <Link
                to={`/category/${anime.category}`}
                className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer"
              >
                更多{anime.category} <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
              {relatedAnimes.map((a) => (
                <Link
                  key={a.id}
                  to={`/anime/${a.id}`}
                  className="group block cursor-pointer"
                  data-product-shop
                >
                  <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
                    <img
                      src={a.coverImage}
                      alt={`${a.title} - 51动漫在线观看`}
                      title={`${a.title} - 51动漫在线观看免费全集`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-foreground-900/80 to-transparent p-3">
                      <span className="text-white text-[10px] font-medium">{a.status === '连载中' ? `更新至${a.episodes}话` : `共${a.episodes}话`}</span>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">{a.title}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <StarRating rating={a.rating} />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      <i className="ri-star-fill text-accent-500 text-xs"></i>
      <span className="text-xs text-foreground-500">{rating}</span>
    </div>
  );
}